import React, { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { Navigate, useNavigate } from 'react-router-dom'

import { AxiosError } from 'axios'
import { isEqual } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { AlertBannerTypes } from '@lyrahealth-inc/shared-app-logic/src/features/alerts/AlertBannerTypes'
import { useIsMounted } from '@lyrahealth-inc/shared-app-logic/src/features/common/hooks/useIsMounted'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { Episode } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'
import {
  SafetyPlan,
  SafetyPlanBody,
  SafetyPlanCrisisContactOption,
  SafetyPlanStatus,
} from '@lyrahealth-inc/shared-app-logic/src/models/safetyPlan/SafetyPlan'
import { BodyText, Size as BodyTextSize } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { CheckboxGroup } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/CheckboxGroup'
import { InputField } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/InputField'
import { AttentionIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/AttentionIcon'
import { LoadingIndicator } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/LoadingIndicator'
import { Link } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/link/Link'
import { PrimaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/primaryButton/PrimaryButton'
import { SecondaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/secondaryButton/SecondaryButton'
import { TextButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/textButton/TextButton'
import { Title, Size as TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { useDebouncedCallback } from '@lyrahealth-inc/ui-core-crossplatform/src/hooks/useDebouncedCallback'
import { AlertBanner } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/alertBanner/AlertBanner'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import safetyPlanApi, { useCreateSafetyPlanMutation, useUpdateSafetyPlanMutation } from './data/safetyPlanApi'
import { useClientSafetyPlan } from './data/useClientSafetyPlan'
import { CLIENT_HOME, CLIENT_SAFETY_PLAN } from '../../common/constants/routingConstants'
import { getSelectedClientId } from '../../data/lyraTherapy/clientSelectors'
import { useAppDispatch } from '../../data/store'
import { ACTIVE_ALERT_HEIGHT, JOIN_BANNER_HEIGHT, useAllBannerVisibility } from '../../hooks/useAllBannerVisibility'
import { useSelectedClientData } from '../clients/clientDetails/data/hooks/useSelectedClientData'
import { setToastContent } from '../data/ltToastAutoActions'
import { useSelectedEpisodeForClient } from '../episodes/data/hooks/useSelectedEpisodeForClient'

const NO_MARGIN = { marginBottom: 0 }

const AUTOSAVE_DELAY_MS = 1000

type TextFieldKey = Exclude<keyof SafetyPlanBody, 'professionalResources'>

const trimBody = (body: SafetyPlanBody): SafetyPlanBody =>
  Object.fromEntries(
    Object.entries(body)
      .map(([key, value]) => [key, typeof value === 'string' ? value.trim() : value])
      .filter(([, value]) => value?.length),
  )

const withLyraLine = (body: SafetyPlanBody): SafetyPlanBody => {
  const resources = body.professionalResources ?? []
  return resources.includes(SafetyPlanCrisisContactOption.LYRA_24_7_LINE)
    ? body
    : { ...body, professionalResources: [SafetyPlanCrisisContactOption.LYRA_24_7_LINE, ...resources] }
}

const NEW_PLAN = trimBody(withLyraLine({}))

const toMergePatch = (saved: SafetyPlanBody, next: SafetyPlanBody) => ({
  ...Object.fromEntries(Object.keys(saved).map((key) => [key, null])),
  ...next,
})

const StickyTop = styled.View<{ topOffset: number }>(({ topOffset }) => ({
  position: 'sticky',
  top: `${topOffset}px`,
  zIndex: 11,
}))

const Header = styled.View<{ theme: ThemeType }>(({ theme: { colors, spacing } }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: spacing['40px'],
  padding: spacing['24px'],
  backgroundColor: colors.backgroundPrimary,
  borderBottomWidth: 1,
  borderBottomColor: colors.borderDefault,
}))

const HeaderTitle = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  flex: 1,
  gap: spacing['4px'],
}))

const HeaderActions = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: spacing['16px'],
}))

const ExitButton = styled(TextButton)({ alignSelf: 'center' })

const Page = styled.View<{ theme: ThemeType }>(({ theme: { colors } }) => ({
  flexGrow: 1,
  backgroundColor: colors.backgroundElement,
}))

const Form = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  width: '100%',
  maxWidth: 1024,
  marginHorizontal: 'auto',
  gap: spacing['40px'],
  padding: spacing['48px'],
}))

const SectionHeader = styled.View({})

const Section = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  gap: spacing['24px'],
}))

const LoadError = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  alignItems: 'center',
  gap: spacing['16px'],
  padding: spacing['48px'],
}))

const LoadErrorActions = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: spacing['16px'],
}))

const SafetyPlanLoadError: FunctionComponent<{ onRetry: () => void }> = ({ onRetry }) => {
  const { formatMessage } = useIntl()
  const { colors } = useTheme() as ThemeType
  const navigate = useNavigate()

  return (
    <LoadError testID={tID('CreateSafetyPlan-loadError')}>
      <BodyText
        text={formatMessage({
          defaultMessage: "The safety plan couldn't be loaded.",
          description: 'Shown when the client safety plan fails to load',
        })}
        size={BodyTextSize.DEFAULT}
        color={colors.textPrimary}
      />
      <LoadErrorActions>
        <TextButton
          text={formatMessage({ defaultMessage: 'Exit', description: 'Leave the create safety plan page' })}
          onPress={() => navigate(CLIENT_HOME.route)}
          testID={tID('CreateSafetyPlan-loadError-exit')}
        />
        <SecondaryButton
          text={formatMessage({ defaultMessage: 'Try again', description: 'Retry loading the safety plan' })}
          onPress={onRetry}
          testID={tID('CreateSafetyPlan-loadError-retry')}
        />
      </LoadErrorActions>
    </LoadError>
  )
}

export const CreateSafetyPlan: FunctionComponent = () => {
  const { isSafetyPlanEnabled } = useFlags()
  const patientId = useSelector(getSelectedClientId)
  const { data: plan, isSuccess, isError, refetch } = useClientSafetyPlan(patientId)
  const [initialPlan, setInitialPlan] = useState<SafetyPlan | null>()
  useEffect(() => {
    if (isSuccess && initialPlan === undefined) setInitialPlan(plan ?? null)
  }, [isSuccess, plan, initialPlan])

  if (!isSafetyPlanEnabled) {
    return <Navigate to={CLIENT_HOME.route} replace />
  }
  if (isError && initialPlan === undefined) {
    return <SafetyPlanLoadError onRetry={refetch} />
  }
  if (initialPlan === undefined) {
    return <LoadingIndicator topPadding={0} />
  }
  if (initialPlan?.status === SafetyPlanStatus.ACTIVE) {
    return <Navigate to={CLIENT_SAFETY_PLAN.route} replace />
  }
  return <SafetyPlanEditor draft={initialPlan ?? undefined} />
}

const SafetyPlanEditor: FunctionComponent<{ draft?: SafetyPlan }> = ({ draft }) => {
  const { formatMessage, formatDate, formatTime } = useIntl()
  const { colors } = useTheme() as ThemeType
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { hasAnySessionBanner, hasActiveAlerts } = useAllBannerVisibility()
  const patientId = useSelector(getSelectedClientId)
  const { data: client } = useSelectedClientData()
  const { data: episode } = useSelectedEpisodeForClient() as { data: Episode | undefined }
  const [createSafetyPlan] = useCreateSafetyPlanMutation()
  const [updateSafetyPlan] = useUpdateSafetyPlanMutation()
  const [body, setBody] = useState<SafetyPlanBody>(withLyraLine(draft?.draft_body ?? {}))
  const [lastSavedAt, setLastSavedAt] = useState(draft?.update_date)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [hasConflict, setHasConflict] = useState(false)
  const [autoSaveFailed, setAutoSaveFailed] = useState(false)
  const isConflicted = useRef(false)
  const savedPlan = useRef(draft && { id: draft.id, versionId: draft.version_id ?? 0 })
  const savedBody = useRef(trimBody(draft?.draft_body ?? {}))
  const latestBody = useRef(body)
  // Writes run one at a time so each sends the version_id the previous one returned.
  const writeQueue = useRef(Promise.resolve(true))
  const isMounted = useIsMounted()
  const isShared = useRef(false)
  const unconfirmedBodies = useRef<SafetyPlanBody[]>([])

  const showErrorToast = useCallback(
    (text: string) => dispatch(setToastContent({ text, toastType: 'error', id: 'CreateSafetyPlan-error' })),
    [dispatch],
  )

  const fetchCurrentPlan = useCallback(
    async (patientId: string) => {
      const request = dispatch(
        safetyPlanApi.endpoints.getClientSafetyPlans.initiate({ patientId }, { forceRefetch: true }),
      )
      try {
        const plans = await request.unwrap()
        return plans.find(({ status }) => status !== SafetyPlanStatus.EXPIRED)
      } finally {
        request.unsubscribe()
      }
    },
    [dispatch],
  )

  const save = useCallback(
    (content: SafetyPlanBody, submit = false) => {
      writeQueue.current = writeQueue.current
        .then(async () => {
          if (!patientId) return true
          const trimmed = trimBody(content)
          const changed = !isEqual(trimmed, savedBody.current) || unconfirmedBodies.current.length > 0
          if (
            isShared.current ||
            isConflicted.current ||
            (!submit && !changed) ||
            (!savedPlan.current && isEqual(trimmed, NEW_PLAN))
          )
            return true

          const write = (sendBody: boolean) =>
            (savedPlan.current
              ? updateSafetyPlan({
                  id: savedPlan.current.id,
                  versionId: savedPlan.current.versionId,
                  body: sendBody ? toMergePatch(savedBody.current, trimmed) : undefined,
                  submit,
                }).unwrap()
              : createSafetyPlan({ patientId, body: trimmed, submit, originatingEpisodeId: episode?.id }).unwrap()
            ).catch((error) => {
              if ((error as AxiosError).response?.status !== 409) unconfirmedBodies.current.push(trimmed)
              throw error
            })

          let plan: SafetyPlan
          try {
            plan = await write(changed)
          } catch (error) {
            if ((error as AxiosError).response?.status !== 409) throw error
            const latest = await fetchCurrentPlan(patientId)
            const serverBody = trimBody(latest?.draft_body ?? latest?.body ?? {})
            if (!latest || ![trimmed, ...unconfirmedBodies.current].some((sent) => isEqual(serverBody, sent))) {
              isConflicted.current = true
              setHasConflict(true)
              throw error
            }
            savedPlan.current = { id: latest.id, versionId: latest.version_id ?? 0 }
            savedBody.current = serverBody
            const isCurrent = isEqual(serverBody, trimmed)
            const isSharedAsIs = latest.status === SafetyPlanStatus.ACTIVE && !latest.draft_body
            plan = isCurrent && (!submit || isSharedAsIs) ? latest : await write(!isCurrent)
          }
          unconfirmedBodies.current = []
          savedPlan.current = { id: plan.id, versionId: plan.version_id ?? 0 }
          savedBody.current = trimmed
          setLastSavedAt(plan.update_date)
          setAutoSaveFailed(false)

          if (submit) {
            isShared.current = true
            dispatch(
              setToastContent({
                text: formatMessage({
                  defaultMessage: 'Safety plan shared with client',
                  description: 'Toast shown after a safety plan is submitted and shared',
                }),
                toastType: 'success',
                id: 'CreateSafetyPlan-success',
              }),
            )
            navigate(CLIENT_HOME.route)
          }
          return true
        })
        .catch(() => {
          if (isConflicted.current) return false
          if (!submit && isMounted()) {
            setAutoSaveFailed(true)
            return false
          }
          showErrorToast(
            submit
              ? formatMessage({
                  defaultMessage: "The safety plan couldn't be shared. Please try again.",
                  description: 'Toast shown when submitting a safety plan fails',
                })
              : formatMessage({
                  defaultMessage: "Your last changes to the safety plan weren't saved.",
                  description: 'Toast shown when the final auto-save after leaving the safety plan fails',
                }),
          )
          return false
        })
        .finally(() => {
          if (submit && !isShared.current) setIsSubmitting(false)
        })
      return writeQueue.current
    },
    [
      createSafetyPlan,
      updateSafetyPlan,
      fetchCurrentPlan,
      isMounted,
      patientId,
      episode?.id,
      dispatch,
      formatMessage,
      navigate,
      showErrorToast,
    ],
  )

  const autosave = useDebouncedCallback(save, AUTOSAVE_DELAY_MS)

  const changeBody = (next: SafetyPlanBody) => {
    latestBody.current = next
    setBody(next)
    autosave(next)
  }

  useEffect(() => () => void save(latestBody.current), [save])

  const onExit = async () => {
    autosave.cancel()
    if (await save(latestBody.current)) navigate(CLIENT_HOME.route)
  }

  const sections: { key: TextFieldKey; label: string; question: string; placeholder: string }[] = [
    {
      key: 'warningSigns',
      label: formatMessage({ defaultMessage: 'Warning signs', description: 'Safety plan section label' }),
      question: formatMessage({
        defaultMessage: 'What are warning signs I might be going into crisis?',
        description: 'Safety plan question about crisis warning signs',
      }),
      placeholder: formatMessage({
        defaultMessage: 'e.g., Fight with partner',
        description: 'Example answer for safety plan warning signs',
      }),
    },
    {
      key: 'internalCopingStrategies',
      label: formatMessage({ defaultMessage: 'Coping strategies', description: 'Safety plan section label' }),
      question: formatMessage({
        defaultMessage: 'What can I do to take my mind off my thoughts and feelings?',
        description: 'Safety plan question about coping strategies',
      }),
      placeholder: formatMessage({
        defaultMessage: 'e.g., Play a game on my phone',
        description: 'Example answer for safety plan coping strategies',
      }),
    },
    {
      key: 'crisisContacts',
      label: formatMessage({ defaultMessage: 'Crisis contacts', description: 'Safety plan section label' }),
      question: formatMessage({
        defaultMessage: 'Who can I call when I’m in a crisis?',
        description: 'Safety plan question about crisis contacts',
      }),
      placeholder: formatMessage({
        defaultMessage: 'e.g., Family member, friend or medical professional',
        description: 'Example answer for safety plan crisis contacts',
      }),
    },
    {
      key: 'environmentalSafety',
      label: formatMessage({ defaultMessage: 'Safe actions', description: 'Safety plan section label' }),
      question: formatMessage({
        defaultMessage: 'How will I make my space safe?',
        description: 'Safety plan question about making the environment safe',
      }),
      placeholder: formatMessage({
        defaultMessage: 'e.g., Give anything harmful to a friend or family member',
        description: 'Example answer for safety plan safe actions',
      }),
    },
    {
      key: 'planBarriers',
      label: formatMessage({ defaultMessage: 'Plan ahead', description: 'Safety plan section label' }),
      question: formatMessage({
        defaultMessage: 'What stops me from using this plan and what can I do?',
        description: 'Safety plan question about barriers to using the plan',
      }),
      placeholder: formatMessage({
        defaultMessage: 'e.g., If it’s not working, I can remind myself I may not feel better right away',
        description: 'Example answer for safety plan barriers',
      }),
    },
    {
      key: 'additionalNotes',
      label: formatMessage({ defaultMessage: 'Additional notes', description: 'Safety plan section label' }),
      question: formatMessage({
        defaultMessage: 'What else is important for me to remember in a crisis?',
        description: 'Safety plan question for additional notes',
      }),
      placeholder: '',
    },
  ]

  const crisisContactOptions = [
    {
      id: SafetyPlanCrisisContactOption.LYRA_24_7_LINE,
      text: formatMessage({
        defaultMessage: 'Lyra Health 24/7 Line: 1-877-505-7147',
        description: 'Safety plan crisis contact option',
      }),
      readOnly: true,
    },
    {
      id: SafetyPlanCrisisContactOption.NATIONAL_SUICIDE_HOTLINE,
      text: formatMessage({
        defaultMessage: 'National Suicide Hotline: 1-800-273-TALK (8255) or text Crisis Line at 741741',
        description: 'Safety plan crisis contact option',
      }),
    },
    {
      id: SafetyPlanCrisisContactOption.NATIONAL_DOMESTIC_VIOLENCE_HOTLINE,
      text: formatMessage({
        defaultMessage: 'National Domestic Violence Hotline: 1-800-799-SAFE (7233)',
        description: 'Safety plan crisis contact option',
      }),
    },
    {
      id: SafetyPlanCrisisContactOption.EMERGENCY_911,
      text: formatMessage({ defaultMessage: '911', description: 'Safety plan crisis contact option' }),
    },
    {
      id: SafetyPlanCrisisContactOption.LOCAL_MEDICAL_PROVIDER,
      text: formatMessage({
        defaultMessage: 'Local medical provider',
        description: 'Safety plan crisis contact option',
      }),
    },
    {
      id: SafetyPlanCrisisContactOption.LOCAL_EMERGENCY_SERVICES,
      text: formatMessage({
        defaultMessage: 'Local emergency services',
        description: 'Safety plan crisis contact option',
      }),
    },
  ]

  const hasChanges = !isEqual(trimBody(body), NEW_PLAN)

  const onSubmit = () => {
    autosave.cancel()
    setIsSubmitting(true)
    save(body, true)
  }

  const clientName = [client?.preferred_first_name || client?.first_name, client?.last_name].filter(Boolean).join(' ')
  const topOffset = (hasAnySessionBanner ? JOIN_BANNER_HEIGHT : 0) + (hasActiveAlerts ? ACTIVE_ALERT_HEIGHT : 0)

  return (
    <Page>
      <StickyTop topOffset={topOffset}>
        <Header testID={tID('CreateSafetyPlan-header')}>
          <HeaderTitle>
            <Title
              text={formatMessage(
                { defaultMessage: 'Safety plan for {clientName}', description: 'Create safety plan page title' },
                { clientName },
              )}
              size={TitleSize.LARGE}
            />
            {lastSavedAt && (
              <BodyText
                testID={tID('CreateSafetyPlan-autoSaved')}
                text={formatMessage(
                  {
                    defaultMessage: 'Auto-saved {date} • {time}',
                    description: 'When the safety plan draft was last auto-saved',
                  },
                  {
                    date: formatDate(lastSavedAt, { month: 'short', day: 'numeric', year: 'numeric' }),
                    time: formatTime(lastSavedAt, { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' }),
                  },
                )}
                size={BodyTextSize.DEFAULT}
                color={colors.textSecondary}
              />
            )}
          </HeaderTitle>
          <HeaderActions>
            <ExitButton
              text={formatMessage({ defaultMessage: 'Exit', description: 'Leave the create safety plan page' })}
              onPress={onExit}
              testID={tID('CreateSafetyPlan-exit')}
            />
            <PrimaryButton
              text={formatMessage({
                defaultMessage: 'Submit and share',
                description: 'Submit the safety plan and share it with the client',
              })}
              onPress={onSubmit}
              disabled={!hasChanges || hasConflict}
              loading={isSubmitting}
              testID={tID('CreateSafetyPlan-submit')}
            />
          </HeaderActions>
        </Header>
        {(hasConflict || autoSaveFailed) && (
          <AlertBanner
            show
            style={AlertBannerTypes.WARNING}
            icon={<AttentionIcon size={20} />}
            includeX={!hasConflict}
            dismissHandler={() => setAutoSaveFailed(false)}
            testID={tID('CreateSafetyPlan-saveError')}
            contents={
              hasConflict
                ? formatMessage({
                    defaultMessage:
                      'This safety plan was changed somewhere else, so your latest edits weren’t saved. Reload the page to see the current plan.',
                    description: 'Banner shown when the safety plan was edited elsewhere and auto-save stopped',
                  })
                : formatMessage({
                    defaultMessage: "We're having trouble saving",
                    description: 'Banner shown when auto-saving a safety plan draft fails',
                  })
            }
            trailingLink={
              hasConflict ? undefined : (
                <Link
                  text={formatMessage({
                    defaultMessage: 'Try again',
                    description: 'Retry auto-saving the safety plan draft',
                  })}
                  color={colors.components.alertBanner.warning.text}
                  onPress={() => save(latestBody.current)}
                  underline
                />
              )
            }
          />
        )}
      </StickyTop>
      <Form testID={tID('CreateSafetyPlan')}>
        <BodyText
          text={formatMessage({
            defaultMessage:
              "A crisis is a short-term situation where we feel overwhelmed and don't always know what to do. Use this safety plan to get through a tough situation in a skillful way that doesn't make things worse.",
            description: 'Introduction at the top of the safety plan',
          })}
          size={BodyTextSize.LARGE}
          color={colors.textPrimary}
        />
        {sections.map(({ key, label, question, placeholder }) => (
          <Section key={key}>
            <SectionHeader>
              <BodyText text={label} size={BodyTextSize.SMALL} color={colors.textSecondary} />
              <Title text={question} size={TitleSize.MEDIUM} />
            </SectionHeader>
            <InputField
              name={key}
              value={body[key] ?? ''}
              onChange={({ nativeEvent: { text } }) => changeBody({ ...body, [key]: text })}
              placeholder={placeholder}
              accessibilityLabel={question}
              multiline
              numberOfLines={3}
              baseInputStyle={NO_MARGIN}
              testID={tID(`CreateSafetyPlan-${key}`)}
            />
            {key === 'crisisContacts' && (
              <CheckboxGroup
                name='professionalResources'
                label={formatMessage({
                  defaultMessage: 'Additional crisis contacts (select all that apply)',
                  description: 'Label for the list of crisis hotlines and services on the safety plan',
                })}
                options={crisisContactOptions}
                baseInputStyle={NO_MARGIN}
                value={body.professionalResources ?? []}
                onChange={(value) => changeBody(withLyraLine({ ...body, professionalResources: value as string[] }))}
              />
            )}
          </Section>
        ))}
      </Form>
    </Page>
  )
}

export default CreateSafetyPlan
